import React from 'react';
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Zap, Flame } from 'lucide-react';
import AnimatedCounter from "@/components/AnimatedCounter.jsx";
import { useBrandConfig } from "@/features/brandDemo/BrandConfigContext.jsx";

const RankBadge = ({ rank, isMe, brand }) => {
    let style = {};
    let className = "shrink-0 w-8 h-8 rounded-full flex justify-center items-center text-sm font-black mr-3 transition-transform ";

    if (isMe) {
        style = { backgroundColor: brand.colors.accent, color: brand.colors.accentText, boxShadow: `0 4px 15px ${brand.colors.accent}66` };
        className += "scale-110 z-10";
    } else if (rank === 1) {
        style = { backgroundColor: brand.colors.highlight, color: brand.colors.highlightText, boxShadow: `0 4px 15px ${brand.colors.highlight}66` };
        className += "scale-105";
    } else if (rank === 2) {
        className += "bg-white/80 text-black/60 shadow-sm";
    } else if (rank === 3) {
        style = { backgroundColor: brand.colors.goalFlash, color: '#ffffff', boxShadow: `0 4px 15px ${brand.colors.goalFlash}66` };
    } else {
        className += "bg-white/40 text-black/40";
    }

    return <div className={className} style={style}>{rank}</div>;
};

const DemoFootballRow = ({ row, onClick, brand }) => {
    const { t } = useTranslation();
    const isMeRow = row.isMe;
    const isTop3 = row.rank <= 3;

    const highlightClass = isMeRow
        ? "bg-white shadow-[0_8px_25px_rgba(0,0,0,0.1)] z-20 scale-[1.02]"
        : "bg-white/30 hover:bg-white/50 cursor-pointer";
    const fadedClass = row.isFaded ? "opacity-50" : "";

    const getRiskData = (points) => {
        if (points >= 800) return { Icon: Flame,       iconColor: brand.colors.goalFlash };
        if (points >= 400) return { Icon: Zap,         iconColor: brand.colors.highlight };
        return              { Icon: ShieldCheck, iconColor: brand.colors.accent };
    };

    const risk = getRiskData(row.points);

    return (
        <motion.div
            layout
            onClick={onClick}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`flex items-center p-3 rounded-2xl mb-2 relative transition-all duration-300 ${highlightClass} ${fadedClass}`}
        >
            <RankBadge rank={row.rank} isMe={isMeRow} brand={brand} />

            <div className={`flex-1 font-bold truncate min-w-0 mr-2 tracking-wide ${isMeRow ? 'text-black text-sm' : (isTop3 ? 'text-black/80 text-sm' : 'text-black/60 text-xs')}`}>
                {row.pseudo}
            </div>

            <div className={`flex items-center gap-1.5 shrink-0 whitespace-nowrap text-[11px] font-black px-3 py-1.5 rounded-xl mr-3 tracking-widest
                ${isMeRow ? 'bg-black/5 text-black' : 'bg-white/40 border border-white/60 text-black/70'}`}
            >
                <risk.Icon size={14} strokeWidth={3} style={{ color: risk.iconColor }} />
                <span className="pt-px">{row.betDisplay}</span>
            </div>

            <div className="flex items-baseline gap-1">
                <AnimatedCounter
                    value={row.points}
                    className={`shrink-0 font-heading italic tracking-tighter text-right min-w-10 leading-none
                        ${isMeRow ? 'z-20 text-3xl drop-shadow-sm' : (isTop3 ? 'text-black text-2xl' : 'text-black/40 text-xl')}`}
                    style={isMeRow ? { color: brand.colors.accent } : {}}
                    enableEffect={isMeRow}
                    ptsClass="hidden"
                />
                <span className="text-[9px] font-black uppercase tracking-widest" style={isMeRow ? { color: brand.colors.accent } : { color: 'rgba(0,0,0,0.3)' }}>
                    {t('holy.betting.pts')}
                </span>
            </div>
        </motion.div>
    );
};

export const DemoStepLeaderboard = ({
    leaderboard,
    isGoalFlashing,
    setSelectedPlayer = () => {}
}) => {
    const { t } = useTranslation();
    const { brand } = useBrandConfig();

    return (
        <div className="mt-6 text-center animate-fade-in pb-24 font-body max-w-sm mx-auto w-full relative z-30">
            <div className="animate-fade-in mb-4">
                {leaderboard.length > 0 && (
                    <div className="flex justify-center mb-5">
                        <span className="text-[9px] font-black text-black/40 uppercase tracking-[0.15em]">
                            {t('leaderboard.tie_breaker_speed')}
                        </span>
                    </div>
                )}

                <div className={`relative bg-white/20 backdrop-blur-3xl rounded-[2.5rem] p-3 shadow-[0_15px_40px_-10px_rgba(0,0,0,0.1)] text-left overflow-hidden transition-all duration-500 ${isGoalFlashing ? 'animate-shuffle scale-[1.01]' : ''}`}>
                    <AnimatePresence>
                        {leaderboard.map((row) => {
                            if (row.isSeparator) {
                                return (
                                    <motion.div layout key={row.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-center py-3">
                                        <div className="w-1.5 h-1.5 rounded-full bg-black/10 mx-1" />
                                        <div className="w-1.5 h-1.5 rounded-full bg-black/10 mx-1" />
                                        <div className="w-1.5 h-1.5 rounded-full bg-black/10 mx-1" />
                                    </motion.div>
                                );
                            }
                            return (
                                <DemoFootballRow
                                    key={row.id}
                                    row={row}
                                    onClick={() => setSelectedPlayer(row)}
                                    brand={brand}
                                />
                            );
                        })}
                    </AnimatePresence>

                    {leaderboard.length === 0 && (
                        <div className="text-center py-16 flex flex-col items-center justify-center">
                            <div className="w-12 h-12 bg-white/40 rounded-full flex items-center justify-center mb-3">
                                <span className="text-xl opacity-50">👻</span>
                            </div>
                            <span className="text-[10px] font-black text-black/40 uppercase tracking-[0.2em]">
                                {t('leaderboard.empty')}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
